export interface IThirdPartyTrackerTypeOption {
    id: string;
    name: string;
}

export class ThirdPartyTrackerTypeOption implements IThirdPartyTrackerTypeOption {
    id: string;
    name: string;

    constructor()
    constructor(obj: IThirdPartyTrackerTypeOption)
    constructor(obj?: IThirdPartyTrackerTypeOption) {
        this.id = obj?.id || undefined;
        this.name = obj?.name || undefined;
    }
}

export enum ThirdPartyTrackerType {
    IMPRESSION = 0,
    MOAT = 1,
    IAS_VIEWABILITY = 2,
    OTHER_VIEWABILITY = 3
}

const thirdPartyTrackerTypeToIdMap: Map<ThirdPartyTrackerType, string> = new Map([
    [ThirdPartyTrackerType.IMPRESSION, 'Impression'],
    [ThirdPartyTrackerType.MOAT, 'moat'],
    [ThirdPartyTrackerType.IAS_VIEWABILITY, 'IasViewability'],
    [ThirdPartyTrackerType.OTHER_VIEWABILITY, 'OtherViewability'],
]);

const thirdPartyTrackerTypeToNameMap: Map<ThirdPartyTrackerType, string> = new Map([
    [ThirdPartyTrackerType.IMPRESSION, 'Impression tracker'],
    [ThirdPartyTrackerType.MOAT, 'MOAT'],
    [ThirdPartyTrackerType.IAS_VIEWABILITY, 'Viewability tracker (IAS)'],
    [ThirdPartyTrackerType.OTHER_VIEWABILITY, 'Other viewability tracker'],
]);

function isDOMScriptTag(value: string): boolean {
    const regExResult = value.toLowerCase().match(/^(<script (.|\n)*>(.|\n)*<\/script>)|(<iframe ((.|\n)*)>(.|\n)*<\/iframe>)$/gm);
    return regExResult && regExResult.length === 1 ? true : false;
}

function isDOMImgTag(value: string): boolean {
    const regExResult = value.toLowerCase().match(/^<img [^>]*src=("(htt(p|ps):\/\/([^">]+))[^"]*"|'(htt(p|ps):\/\/([^">]+))[^"]*')[^>]*>$/gm);
    return regExResult && regExResult.length === 1 ? true : false;
}

function isUrlString(value: string): boolean {
    const regExResult = value.toLowerCase().match(/^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)?/gm);
    return regExResult && regExResult.length === 1 ? true : false;
}

export const thirdPartyTrackerTagToThirdPartyTrackerTypeOptionRules: Map<ThirdPartyTrackerType, (value: string, timeoutT: number) => Promise<boolean>> = new Map([
    [
        ThirdPartyTrackerType.IMPRESSION,
        (value, timeoutT) => new Promise<boolean>(async (resolve, reject) => {
            const timeout = timeoutT || 5000;
            var timer = null;
            let resolved = false;
            const isUrl = isUrlString(value);
            if (isUrl) {
                const img = new Image();

                img.onload = () => { if (resolved) return; resolved = true; resolve(true); };
                img.onerror = img.onabort = () => { if (resolved) return; resolved = true; resolve(false); };

                timer = setTimeout(function () {
                    if (resolved)
                        return;

                    resolved = true;
                    // reset .src to invalid URL so it stops previous
                    // loading, but doesn't trigger new load
                    img.src = "//!!!!/test.jpg";

                    resolve(false);
                }, timeout);

                img.src = value;
            } else {
                const valueIsImageDomTag = isDOMImgTag(value);
                let isValid = valueIsImageDomTag;

                resolve(isValid);
            }
        })
    ],
    [
        ThirdPartyTrackerType.IAS_VIEWABILITY,
        (value, timeoutT) => new Promise<boolean>(async (resolve, reject) => {
            let result = false;

            const valueIsScriptDomTag = isDOMScriptTag(value);
            if (valueIsScriptDomTag) {
                result = value.toLowerCase().indexOf("adsafeprotected") != -1;
            }

            resolve(result);
        })
    ],
    [
        ThirdPartyTrackerType.MOAT,
        (value, timeoutT) => new Promise<boolean>(async (resolve, reject) => {
            let result = false;

            resolve(result); return; // disabled, not implemented

            const valueIsScriptDomTag = isDOMScriptTag(value);

            if (valueIsScriptDomTag) {
                result = value.toLowerCase().indexOf("moatads") != -1;
            }

            resolve(result);
        })
    ],
    [
        ThirdPartyTrackerType.OTHER_VIEWABILITY,
        (value, timeoutT) => new Promise<boolean>(async (resolve, reject) => {
            let result = false;

            const valueIsScriptDomTag = isDOMScriptTag(value);
            if (valueIsScriptDomTag) {
                result = true;
            }

            resolve(result);
        })
    ],
]);

Object.freeze(ThirdPartyTrackerType);
Object.freeze(thirdPartyTrackerTypeToIdMap);
Object.freeze(thirdPartyTrackerTypeToNameMap);
Object.freeze(thirdPartyTrackerTagToThirdPartyTrackerTypeOptionRules);

export class ThirdPartyTrackerValidator {
    thirdPartyTrackerType: ThirdPartyTrackerType;

    constructor(thirdPartyTrackerType: ThirdPartyTrackerType) {
        this.thirdPartyTrackerType = thirdPartyTrackerType;
    }

    async validateTagAsync(value: string): Promise<boolean> {
        if (!thirdPartyTrackerTagToThirdPartyTrackerTypeOptionRules.has(this.thirdPartyTrackerType)) {
            return true;
        }

        return (thirdPartyTrackerTagToThirdPartyTrackerTypeOptionRules.get(this.thirdPartyTrackerType))(value, 10000);
    }
}

export class ThirdPartyTrackerTypeOptionBuilder {
    id: string;
    name: string;

    constructor(value: ThirdPartyTrackerType) {
        if (!thirdPartyTrackerTypeToIdMap.has(value)) {
            throw `ThirdPartyTrackerType ${value} is not mapped to a property named 'id'`;
        }
        if (!thirdPartyTrackerTypeToNameMap.has(value)) {
            throw `ThirdPartyTrackerType ${value} is not mapped to a property named 'name'`;
        }

        this.id = thirdPartyTrackerTypeToIdMap.get(value);
        this.name = thirdPartyTrackerTypeToNameMap.get(value);
    }

    build(): IThirdPartyTrackerTypeOption {
        return new ThirdPartyTrackerTypeOption({
            id: this.id,
            name: this.name,
        });
    }
}

export class ThirdPartyTrackerTypeBuilder {
    async fromTagAsync(value: string): Promise<ThirdPartyTrackerType | null> {
        let result: ThirdPartyTrackerType = null;

        if (await new ThirdPartyTrackerValidator(ThirdPartyTrackerType.IMPRESSION).validateTagAsync(value)) {
            result = ThirdPartyTrackerType.IMPRESSION;
        } else if (await new ThirdPartyTrackerValidator(ThirdPartyTrackerType.IAS_VIEWABILITY).validateTagAsync(value)) {
            result = ThirdPartyTrackerType.IAS_VIEWABILITY;
        } else if (await new ThirdPartyTrackerValidator(ThirdPartyTrackerType.MOAT).validateTagAsync(value)) {
            result = ThirdPartyTrackerType.MOAT;
        } else {
            if (await new ThirdPartyTrackerValidator(ThirdPartyTrackerType.OTHER_VIEWABILITY).validateTagAsync(value)) {
                result = ThirdPartyTrackerType.OTHER_VIEWABILITY;
            }
        }

        return result;
    }
}

